import React from 'react';
import { Avatar, Switch, Tooltip } from 'antd';
import type { IPracticeByProviderRow } from 'types/marketing';
import {
    isProviderBudgetExhausted,
    isProviderListed,
    getListingStatusTooltip,
    getListingStatusTag,
} from 'utils/marketing';
import BudgetExhaustedTag from 'pages/dashboard/Marketing/components/BudgetExhaustedTag';
import s from './s.module.less';

export interface BudgetSummary {
    cap: number;
    providerCount: number;
    perProvider: number;
    periodLabel: string;
}

interface GroupBudgetDistributionProps {
    providers: IPracticeByProviderRow[];
    budgetExcluded: Set<number>;
    budgetSummary: BudgetSummary;
    stdListingCount: number;
    hasJoinedProListing: boolean;
    activeProviderCount: number;
    poolExhausted: boolean;
    // When omitted / readOnly, the per-provider toggles are rendered non-interactive.
    onToggleProvider?: (providerId: number) => void;
    readOnly?: boolean;
}

const getInitials = (name: string) => {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    return (parts[0]?.[0] ?? '').toUpperCase();
};

// Per-provider "Budget distribution" list — the practice pool budget split evenly across active providers.
// Extracted from GroupCampaignSetup so the Ops group-practice view can render the same list read-only.
const GroupBudgetDistribution: React.FC<GroupBudgetDistributionProps> = ({
    providers,
    budgetExcluded,
    budgetSummary,
    stdListingCount,
    hasJoinedProListing,
    activeProviderCount,
    poolExhausted,
    onToggleProvider,
    readOnly = false,
}) => (
    <div className={s.budgetDistSection}>
        <div className={s.budgetDistHeader}>
            <div className={s.budgetDistTitle}>Budget distribution</div>
            <span className={s.budgetDistAutoBadge}>Auto</span>
        </div>
        <div className={s.budgetDistBody}>
            <div className={s.budgetDistSummary}>
                <div className={s.budgetDistSummaryText}>
                    <strong>
                        ${budgetSummary.cap.toLocaleString()}/{budgetSummary.periodLabel}
                    </strong>{' '}
                    practice budget distributed evenly across{' '}
                    <strong>
                        {budgetSummary.providerCount} of {stdListingCount} providers
                    </strong>{' '}
                    on Pro-Listing
                </div>
                {budgetSummary.providerCount > 0 && (
                    <div className={s.budgetDistPerProvider}>
                        ${budgetSummary.perProvider.toLocaleString()}/provider
                    </div>
                )}
            </div>
            <div className={s.budgetDistBar}>
                {Array.from({ length: budgetSummary.providerCount }).map((_, i) => {
                    const count = budgetSummary.providerCount;
                    let radius = '0';
                    if (count === 1) radius = '3px';
                    else if (i === 0) radius = '3px 0 0 3px';
                    else if (i === count - 1) radius = '0 3px 3px 0';
                    return (
                        <div
                            // eslint-disable-next-line react/no-array-index-key
                            key={i}
                            className={s.budgetDistBarActive}
                            style={{ flex: 1, borderRadius: radius }}
                        />
                    );
                })}
            </div>
            <div className={s.budgetDistNote}>
                Each provider&apos;s spend is capped at ${budgetSummary.perProvider.toLocaleString()}/
                {budgetSummary.periodLabel}. When one provider hits their cap, remaining budget continues flowing to
                others.
            </div>
            <div className={s.budgetProviderList}>
                {providers.map((p) => {
                    const isOff = budgetExcluded.has(p.providerId);
                    const listed = isProviderListed(p);
                    const listingTag = getListingStatusTag(p.listingStatus);
                    const interactive = !readOnly && listed;
                    return (
                        <div key={p.providerId} className={s.budgetProviderRow}>
                            {p.avatar ? (
                                <Avatar src={p.avatar} size={28} />
                            ) : (
                                <Avatar size={28} className={s.budgetProviderAvatar}>
                                    {getInitials(p.name)}
                                </Avatar>
                            )}
                            <div className={s.budgetProviderInfo}>
                                <div className={s.budgetProviderName}>
                                    {p.name}
                                    <span className={`${s.listingTag} ${s[`listingTag_${listingTag.variant}`]}`}>
                                        {listingTag.label}
                                    </span>
                                </div>
                                <div className={s.budgetProviderSpec}>
                                    {p.services?.map((svc) => svc.specialtyName).join(', ')}
                                </div>
                            </div>
                            {isProviderBudgetExhausted(p) && (
                                <BudgetExhaustedTag
                                    className={s.budgetProviderTag}
                                    pausedSince={p.budgetExhaustedPausedAt}
                                />
                            )}
                            <div className={s.budgetProviderCap}>
                                {isOff || !listed
                                    ? ''
                                    : `$${budgetSummary.perProvider.toLocaleString()}/${budgetSummary.periodLabel}`}
                            </div>
                            {interactive && (
                                <Switch
                                    size="small"
                                    checked={!isOff}
                                    onChange={() => onToggleProvider?.(p.providerId)}
                                />
                            )}
                            {/* Read-only listed: show current state but disabled. */}
                            {!interactive && listed && <Switch size="small" checked={!isOff} disabled />}
                            {!listed && (
                                <Tooltip title={getListingStatusTooltip(p.listingStatus)} placement="leftTop">
                                    <span className={s.disabledSwitchWrap}>
                                        <Switch size="small" checked={false} disabled />
                                    </span>
                                </Tooltip>
                            )}
                        </div>
                    );
                })}
            </div>
            {hasJoinedProListing && activeProviderCount === 0 && !poolExhausted ? (
                <div className={s.budgetPauseCallout}>
                    <svg
                        width="18"
                        height="18"
                        viewBox="0 0 20 20"
                        fill="none"
                        stroke="#D97706"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{ flexShrink: 0, marginTop: 1 }}
                    >
                        <circle cx="10" cy="10" r="8" />
                        <line x1="10" y1="6.5" x2="10" y2="10.5" />
                        <line x1="10" y1="13.5" x2="10" y2="13.5" />
                    </svg>
                    <div>
                        <div className={s.budgetPauseTitle}>No providers selected</div>
                        <div className={s.budgetPauseSub}>
                            Toggle any provider back on to keep bidding live, or click <strong>Save changes</strong> to
                            pause the campaign.
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    </div>
);

export default GroupBudgetDistribution;
